import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Money, Info, ArrowClockwise, Check, X, ShieldCheck, ListBullets, CaretLeft, CaretRight,
} from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import {
  getbranchnames, getChequenumbers, getChequeDetails, clearDateAndStatus, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const th = {
  textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB',
  color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap',
};
const td = { padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px' };

// GetChequenumbers returns only reference_number and clear_status (no payment
// number), so that pair identifies a reference. Each cheque row carries its own
// payment_number once the details load.
function chequeKey(c) {
  return `${c.reference_number}|${c.clear_status}`;
}

function dayPart(value) {
  if (!value) return '—';
  return String(value).split('T')[0].split(' ')[0];
}

function ChequeIssueForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branches, setBranches] = useState([]);
  const [paymentNumbers, setPaymentNumbers] = useState([]);
  const [details, setDetails] = useState([]);
  const [oldDataSnapshot, setOldDataSnapshot] = useState([]);

  const [branchCode, setBranchCode] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [reason, setReason] = useState('');

  const [showGrid, setShowGrid] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const loadRequestRef = useRef(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingBranchName, setPendingBranchName] = useState('');
  const [pendingReferenceNo, setPendingReferenceNo] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  const selectedBranch = branches.find((b) => String(b.branchCode) === String(branchCode)) || null;
  const selectedPayment = paymentNumbers.find((c) => chequeKey(c) === selectedKey) || null;

  useEffect(() => {
    getbranchnames()
      .then((res) => setBranches(Array.isArray(res) ? res : []))
      .catch(() => showToast('error', 'Load Failed', 'Failed to load branches. Please try again.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromReference = () => {
    loadRequestRef.current += 1; // drop any cheque load still in flight
    setLoadingDetails(false);
    setDetails([]);
    setOldDataSnapshot([]);
    setShowGrid(false);
    setPage(0);
  };

  const handleBranchChange = async (value) => {
    setBranchCode(value || '');
    setSelectedKey('');
    setPaymentNumbers([]);
    resetFromReference();
    if (!value) return;

    try {
      const res = await getChequenumbers(value);
      setPaymentNumbers(Array.isArray(res) ? res : []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('[getChequenumbers] error =', err);
      showToast('error', 'Load Failed', 'Failed to load reference numbers. Please try again.');
    }
  };

  // Picking a reference loads its cheques straight away — there is no Get button.
  // A quick second pick can overtake a slow first one, so only the latest request
  // is allowed to write to the grid.
  const handleReferenceChange = (value) => {
    setSelectedKey(value || '');
    resetFromReference();
    const payment = paymentNumbers.find((c) => chequeKey(c) === value);
    if (payment) loadDetails(payment);
    else loadRequestRef.current += 1;
  };

  const loadDetails = async (payment) => {
    if (!payment || !branchCode) return;

    const requestId = ++loadRequestRef.current;
    setLoadingDetails(true);
    try {
      const res = await getChequeDetails(
        branchCode,
        '', // GetchequeDetails filters on reference_number alone
        payment.reference_number,
        payment.clear_status,
      );
      if (requestId !== loadRequestRef.current) return;
      const rows = (Array.isArray(res) ? res : []).map((d) => ({ ...d, selected: false }));
      setDetails(rows);
      if (isEmptyResult(rows)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      setOldDataSnapshot(rows.map(({ selected, ...rest }) => rest));
      setShowGrid(true);
    } catch (err) {
      if (requestId !== loadRequestRef.current) return;
      console.error('[getChequeDetails] error =', err);
      setShowGrid(true); // show the "no records" message, same as the Angular screen
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Failed to load cheque details.'));
    } finally {
      if (requestId === loadRequestRef.current) setLoadingDetails(false);
    }
  };

  const toggleRow = (index, checked) => {
    setDetails((rows) => rows.map((r, i) => (i === index ? { ...r, selected: checked } : r)));
  };

  const toggleAllOnPage = (checked, pageRows) => {
    const keys = new Set(pageRows.map((r) => r.__index));
    setDetails((rows) => rows.map((r, i) => (keys.has(i) ? { ...r, selected: checked } : r)));
  };

  const selectedRows = useMemo(() => details.filter((d) => d.selected), [details]);
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);
  const branchInvalid = touched && !branchCode;
  const referenceInvalid = touched && !selectedKey;

  const isValid = !!branchCode && !!selectedKey
    && reason.trim().length >= 10 && reason.length <= 200;

  // ── Save flow: validate + stage, then confirm ────────────────────
  const handleSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (selectedRows.length === 0) {
      showToast('warn', 'No Rows Selected', 'Please select at least one cheque to clear.');
      return;
    }

    const trimmedReason = reason.trim();

    setPendingBranchName(selectedBranch?.branchName ?? '');
    setPendingReferenceNo(selectedPayment?.reference_number ?? '');
    setPendingReason(trimmedReason);
    setPendingRows(selectedRows);

    // Only the cheques actually being cleared are logged, each as it was and as it
    // becomes — rows that were not ticked are not part of this change.
    const changedIndexes = details.reduce((acc, d, i) => (d.selected ? [...acc, i] : acc), []);
    const oldRows = changedIndexes.map((i) => ({
      ...oldDataSnapshot[i],
      clear_status: selectedPayment?.clear_status,
    }));
    const newData = changedIndexes.map((i) => ({
      ...oldDataSnapshot[i],
      clear_date: null,
      clear_status: 'N',
    }));

    setPendingAudit({
      schemaName: branchCode,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'CHEQUE_ISSUE',
      oldData: oldRows,
      newData,
      reason: trimmedReason,
    });

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingAudit(null);
    setPendingRows([]);
  };

  const confirmSave = async () => {
    if (!pendingAudit || pendingRows.length === 0) return;

    setSaving(true);
    try {
      // One call per selected row, exactly like the Angular screen's Promise.all.
      await Promise.all(pendingRows.map((row) => clearDateAndStatus(
        branchCode,
        row.payment_number,
        row.reference_number,
        row.transaction_no ?? '',
        row.tbl_trans_bank_entries_id ?? 0,
        selectedPayment?.clear_status,
      )));

      try {
        await saveData([pendingAudit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Cheques updated and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Cheques updated, but audit log failed to save.', true);
        handleClear();
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('ClearDateandStatus failed:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not update the clear date/status. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setBranchCode('');
    setSelectedKey('');
    setPaymentNumbers([]);
    resetFromReference();
    setReason('');
    setTouched(false);
    setPendingAudit(null);
    setPendingRows([]);
  };

  const totalPages = Math.max(1, Math.ceil(details.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pageRows = details
    .map((r, i) => ({ ...r, __index: i }))
    .slice(currentPage * pageSize, currentPage * pageSize + pageSize);
  const allOnPageSelected = pageRows.length > 0 && pageRows.every((r) => r.selected);

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="cheque-issue-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="cheque-issue-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Money size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Cheque Issue</div>
          </div>
        </div>

        {/* SECTION A: Select Cheque */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Cheque</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose a branch and reference number — the cheques load automatically</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Branch Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`branch-${formKey}`}
              options={branches}
              getLabel={(o) => o.branchName}
              getValue={(o) => o.branchCode}
              value={branchCode}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={branchInvalid}
              testId="cheque-issue-branch"
            />
            {branchInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Reference No. <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`reference-${formKey}`}
              options={paymentNumbers}
              getLabel={(o) => o.reference_number}
              getValue={(o) => chequeKey(o)}
              value={selectedKey}
              onChange={handleReferenceChange}
              placeholder="Select reference no."
              emptyMessage="Select a branch first"
              disabled={!branchCode}
              error={referenceInvalid}
              testId="cheque-issue-reference"
            />
            {referenceInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Reference No. is required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!selectedKey} hint="Select a branch and reference number in section A first">
        {/* SECTION B: Cheque Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Cheque Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Select the rows to clear, then provide a reason below</div>
          </div>
        </div>

        {showGrid && details.length > 0 && (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }} data-testid="cheque-issue-grid">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
              <ListBullets size={14} /> Cheque Details
              <span style={{ fontSize: '10px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>
                {details.length} record(s)
              </span>
              {selectedRows.length > 0 && (
                <span style={{ fontSize: '10px', fontWeight: '700', color: '#065F46', background: '#ECFDF5', padding: '2px 6px', borderRadius: '4px' }}>
                  {selectedRows.length} selected
                </span>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: '56px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={(e) => toggleAllOnPage(e.target.checked, pageRows)}
                        aria-label="Select all rows on this page"
                        data-testid="cheque-issue-select-all"
                      />
                    </th>
                    <th style={{ ...th, width: '44px', textAlign: 'center' }}>#</th>
                    <th style={th}>Payment No.</th>
                    <th style={th}>Reference No.</th>
                    <th style={th}>Payment Date</th>
                    <th style={th}>Paid To</th>
                    <th style={{ ...th, textAlign: 'right' }}>Amount (₹)</th>
                    <th style={th}>Clear Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((d) => (
                    <tr key={`${d.payment_number}-${d.reference_number}-${d.__index}`} style={{ background: d.selected ? '#EFF6FF' : 'transparent' }}>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!d.selected}
                          onChange={(e) => toggleRow(d.__index, e.target.checked)}
                          data-testid="cheque-issue-row-checkbox"
                        />
                      </td>
                      <td style={{ ...td, textAlign: 'center', color: '#94A3B8' }}>{d.__index + 1}</td>
                      <td style={td}>
                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>
                          {d.payment_number}
                        </span>
                      </td>
                      <td style={td}>{d.reference_number}</td>
                      <td style={td}>{dayPart(d.payment_date)}</td>
                      <td style={td}>{d.paid_to ?? '—'}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#DC2626', fontWeight: '600' }}>
                        {Number(d.paid_amount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={td}>{dayPart(d.clear_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {details.length > pageSize && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid #E5E7EB', fontSize: '12px', color: '#6B7280' }}>
                <div>
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, details.length)} of {details.length}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    className="input-field"
                    style={{ fontSize: '12px', width: 'auto', padding: '4px 8px' }}
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                    data-testid="cheque-issue-page-size"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                  <button type="button" className="btn-secondary" style={{ padding: '4px 8px' }} disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                    <CaretLeft size={12} />
                  </button>
                  <span>{currentPage + 1} / {totalPages}</span>
                  <button type="button" className="btn-secondary" style={{ padding: '4px 8px' }} disabled={currentPage >= totalPages - 1} onClick={() => setPage(currentPage + 1)}>
                    <CaretRight size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {showGrid && details.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', border: '1px solid #C7D2FE', background: '#F1F5FF', borderRadius: '8px', fontSize: '12px', color: '#334155' }} data-testid="cheque-issue-empty">
            <Info size={14} /> No records found for the selected reference.
          </div>
        )}

        {!showGrid && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px', border: '1px dashed #E5E7EB', borderRadius: '8px', color: '#9CA3AF', fontSize: '12px' }}>
            <Info size={16} /> {loadingDetails ? 'Loading cheques…' : 'Pick a reference number to load its cheques'}
          </div>
        )}
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!selectedKey || selectedRows.length === 0} hint="Select at least one cheque row in section B first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Explain why this cheque is being cleared..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="cheque-issue-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : 'Reason must be at least 10 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="cheque-issue-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving || selectedRows.length === 0} data-testid="cheque-issue-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '460px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="cheque-issue-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Cheque Clearance</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to clear <strong>{pendingRows.length}</strong> cheque record(s) for reference{' '}
                <strong>{pendingReferenceNo}</strong> at <strong>{pendingBranchName}</strong>. Please review before confirming.
              </p>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="cheque-issue-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="cheque-issue-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChequeIssueForm;
